import React from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Icon from '@amzn/meridian/icon';
import infoIcon from '@amzn/meridian-tokens/base/icon/info';
import CustomAlert from '../../commonComponents/CustomAlert';
import { PLAN_STATUS, PLAN_VERSIONS, TEXTS } from '../../utils/constants';
import { ALERT_BG_WARNING, ALERT_MESSAGES } from './flowPlannerConstants';

const EXPERIMENTATION_BANNER = (
  <CustomAlert backgroundColor={ALERT_BG_WARNING} title="You are on Flow Planner experimentation mode." />
);

export default function PlanStatusAlert({
  planStatus,
  error,
  planId,
  planVersion,
  onRunPlan,
  onReplan,
  onOpenOptimizer,
  timeDistributions,
  isExperimentationMode = false
}) {
  const MODEL_TYPE_LABELS = {
    [PLAN_VERSIONS.DYNAMIC_V2]: 'Math Optimizer',
    [PLAN_VERSIONS.STATIC_V3]: 'Heuristic'
  };
  const modelTypeLabel = MODEL_TYPE_LABELS[planVersion] || null;
  const subtitle = planId ? `(Plan ID: ${planId}${modelTypeLabel ? `, Model Type: ${modelTypeLabel}` : ''})` : '';

  const withExperimentation = (content) =>
    isExperimentationMode ? (
      <Column spacing="200" width="100%">
        {EXPERIMENTATION_BANNER}
        {content}
      </Column>
    ) : (
      content
    );

  if (planStatus === PLAN_STATUS.PENDING_CREATION) {
    return withExperimentation(
      <CustomAlert backgroundColor="highlight" title={ALERT_MESSAGES.GENERATING} subtitle={subtitle}>
        <Row spacing="200" alignmentVertical="center">
          <Loader size="small" />
        </Row>
      </CustomAlert>
    );
  }

  const alertConfig = {
    [PLAN_STATUS.PENDING_APPROVAL]: { bg: ALERT_BG_WARNING, title: ALERT_MESSAGES.AWAITING_APPROVAL },
    [PLAN_STATUS.PENDING_USER_INPUT]: { bg: ALERT_BG_WARNING, title: ALERT_MESSAGES.AWAITING_APPROVAL },
    [PLAN_STATUS.APPROVED]: { title: ALERT_MESSAGES.APPROVED },
    [PLAN_STATUS.REJECTED]: { bg: ALERT_BG_WARNING, title: ALERT_MESSAGES.REJECTED },
    [PLAN_STATUS.FAILED]: { bg: ALERT_BG_WARNING, title: error || ALERT_MESSAGES.CREATION_FAILED }
  };

  const config = alertConfig[planStatus];
  if (!config) {
    return withExperimentation(
      <CustomAlert title={ALERT_MESSAGES.NOT_RUN}>
        <Row spacing="200" alignmentHorizontal="end">
          {timeDistributions}
          <Button size="small" onClick={onRunPlan}>
            Run Plan
          </Button>
          {onOpenOptimizer && (
            <Button type="secondary" size="small" onClick={onOpenOptimizer}>
              Mathematical Optimizer
            </Button>
          )}
        </Row>
      </CustomAlert>
    );
  }

  return withExperimentation(
    <Box
      backgroundColor={config.bg || 'highlight'}
      type="fill"
      spacingInset="300"
      width="100%"
      className="alert-border">
      <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
        <Row spacingInset="200" alignmentVertical="top">
          <Icon tokens={infoIcon} />
          <Column spacing="100">
            <Text>{config.title}</Text>
            <Text type={TEXTS.T4}>{<strong>{subtitle}</strong>}</Text>
          </Column>
        </Row>
        <Row spacing="200" alignmentVertical="center">
          {timeDistributions}
          <Button size="small" onClick={onReplan}>
            Re-run Plan
          </Button>
        </Row>
      </Row>
    </Box>
  );
}
